/**
 * Formatting Utilities
 * Shared formatting functions used across components
 */

/**
 * Format word count for display
 * @param count - Number of words
 * @returns Formatted string like "1.5K words" or "500 words"
 */
export function formatWordCount(count: number): string {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K words`;
  }
  return `${count} words`;
}

/**
 * Estimate reading time based on word count
 * @param wordCount - Number of words to read
 * @param wpm - Words per minute (default: 250)
 * @returns Formatted string like "5 min" or "1h 30m"
 */
export function estimateTime(wordCount: number, wpm: number = 250): string {
  const minutes = Math.ceil(wordCount / wpm);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Format time remaining for display
 * @param minutes - Number of minutes
 * @returns Formatted string like "< 1 min", "30 min", "2h", "2h 30m"
 */
export function formatTimeRemaining(minutes: number): string {
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/**
 * Format a number as a percentage
 * @param current - Current value
 * @param total - Total value
 * @returns Formatted percentage string like "50%"
 */
export function formatProgress(current: number, total: number): string {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  return `${percentage}%`;
}
